'use client';

import React, { useState, useEffect, useRef } from 'react';
import { 
    FolderIcon, 
    DocumentIcon, 
    ArrowUpIcon,
    PlusIcon,
    CloudArrowUpIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    ArrowDownTrayIcon,
    ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import { 
    FolderIcon as FolderSolidIcon,
    DocumentIcon as DocumentSolidIcon
} from '@heroicons/react/24/solid';
import fileManagerServices, { 
    S3Object, 
    ListObjectsRequest,
    ListObjectsResponse 
} from '../../variants/default/utils/services/fileManager';
import FileUploadModal from './FileUploadModal';
import CreateFolderModal from './CreateFolderModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useIdPath } from "@/contexts/IdPathProvider";

interface FileManagerProps {
    namepath: string;
    onFileSelect?: (file: S3Object) => void;
    allowUpload?: boolean;
    allowDelete?: boolean;
    allowDownload?: boolean;
    allowCopy?: boolean;
}

export default function FileManager({ 
    namepath, 
    onFileSelect,
    allowUpload = true,
    allowDelete = false,
    allowDownload = true,
    allowCopy = true
}: FileManagerProps) {
    const { idPath, namePath } = useIdPath();

    const [objects, setObjects] = useState<S3Object[]>([]);
    const [currentPath, setCurrentPath] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const [pathHistory, setPathHistory] = useState<string[]>([]);
    
    // Modals
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedObject, setSelectedObject] = useState<S3Object | null>(null);

    const baseUrl = fileManagerServices.createFolderUrl(namepath);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Load danh sách file/folder
    const loadObjects = async (path: string = '') => {
        setLoading(true);
        setError('');
        
        try {
            // Properly concatenate baseUrl and path
            let folderUrl = baseUrl;
            if (path) {
                // Remove trailing slash from baseUrl and leading slash from path
                const cleanBaseUrl = baseUrl.replace(/\/$/, '');
                const cleanPath = path.replace(/^\//, '');
                folderUrl = `${cleanBaseUrl}/${cleanPath}/`;
            }
            const request: ListObjectsRequest = {
                recursive: false,
                maxKeys: 1000,
                folderUrl
            };

            const response: ListObjectsResponse | any = await fileManagerServices.listObjects(request);
          
            if (response?.success && response.data) {
                // Combine folders and files into a single objects array
                const folders = (response.data.folders || []).map((folder: any) => ({
                    key: folder.path || folder.name,
                    name: folder.name,
                    lastModified: folder.lastModified,
                    size: folder.size,
                    isFolder: true
                }));
                
                const files = (response.data.files || []).map((file: any) => ({
                    key: file.path || file.name,
                    name: file.name,
                    lastModified: file.lastModified,
                    size: file.size,
                    isFolder: false
                }));
                
                const allObjects = [...folders, ...files];
                setObjects(allObjects);
            } else {
                console.error('📂 FileManager: API error:', response);
                setError(response?.error || 'Không thể tải danh sách file');
            }
        } catch (err) {
            console.error('📂 FileManager: Load objects error:', err);
            setError('Có lỗi xảy ra khi tải danh sách file');
        } finally {
            setLoading(false);
        }
    };

    // Load initial data
    useEffect(() => {
        loadObjects();
    }, [namepath]);

    // Navigate to folder
    const navigateToFolder = (folderName: string) => {
        const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
        setPathHistory([...pathHistory, currentPath]);
        setCurrentPath(newPath);
        loadObjects(newPath);
    };

    // Navigate back
    const navigateBack = () => {
        if (pathHistory.length > 0) {
            const previousPath = pathHistory[pathHistory.length - 1];
            setPathHistory(pathHistory.slice(0, -1));
            setCurrentPath(previousPath);
            loadObjects(previousPath);
        }
    };

    // Navigate to root
    const navigateToRoot = () => {
        setPathHistory([]);
        setCurrentPath('');
        loadObjects('');
    };

    // Handle file click
    const handleObjectClick = (obj: S3Object) => {
        if (obj.isFolder) {
            navigateToFolder(obj.name);
        } else {
            onFileSelect?.(obj);
        }
    };

    // Handle upload
    const handleUpload = async (files: File[]) => {
        try {
            let folderUrl = baseUrl;
            // if (currentPath) {
                const cleanBaseUrl = baseUrl.replace(/\/$/, '');
                const cleanPath = currentPath.replace(/^\//, '');
                folderUrl = currentPath ? `${cleanBaseUrl}/${cleanPath}` : cleanBaseUrl;
            // }

            // console.log('Uploading to baseUrl:', baseUrl);
            // console.log('currentPath:', currentPath);
            // console.log('Uploading to folderUrl:', folderUrl);
            
            const response = await fileManagerServices.uploadFiles({
                folderUrl,
                files
            }, idPath);



            if (response.data?.success) {
                loadObjects(currentPath);
                setShowUploadModal(false);
            } else {
                setError(response.data?.error || 'Upload thất bại');
            }
        } catch (err) {
            setError('Có lỗi xảy ra khi upload file');
            console.error('Upload error:', err);
        }
    };

    // Handle create folder
    const handleCreateFolder = async (folderName: string) => {
        try {
            let folderUrl = baseUrl;
            if (currentPath) {
                const cleanBaseUrl = baseUrl.replace(/\/$/, '');
                const cleanPath = currentPath.replace(/^\//, '');
                folderUrl = `${cleanBaseUrl}/${cleanPath}/${folderName}`;
            } else {
                folderUrl = `${baseUrl}${folderName}`;
            }
            
            const response = await fileManagerServices.createFolder({
                folderUrl
            }, idPath);

            if (response?.success) {
                loadObjects(currentPath);
                setShowCreateFolderModal(false);
            } else {
                setError(response?.error || 'Tạo folder thất bại');
            }
        } catch (err) {
            setError('Có lỗi xảy ra khi tạo folder');
            console.error('Create folder error:', err);
        }
    };
    

    // Handle download
    const handleDownload = async (obj: S3Object) => {
        if (obj.isFolder) return; // Cannot download folders
        
        try {
            // Construct the key path for the file
            const keyPath = baseUrl.replace(/\/$/, '') + '/' + (currentPath ? currentPath + '/' : '') + obj.name;
            // Try to get presigned URL
            try {
                const presigned = await fileManagerServices.getPresignedUrl({ key: keyPath }, idPath);
                if (presigned?.success && presigned.url) {
                    // Use an <a> tag to open the presigned URL — avoids popup blockers and is what you requested
                    const anchor = document.createElement('a');
                    anchor.href = presigned.url;
                    anchor.target = '_blank';
                    anchor.rel = 'noopener noreferrer';
                    // download attribute may be ignored for cross-origin URL, but set it regardless
                    anchor.download = obj.name;
                    document.body.appendChild(anchor);
                    anchor.click();
                    document.body.removeChild(anchor);
                    return;
                } else {
                    setError(presigned?.message || 'Tải file thất bại');
                }
            } catch (err) {
                console.warn('Presigned URL failed, fallback to read-file:', err);
            }

            // Fallback: Create a simple download link using <a> tag
            const a = document.createElement('a');
            a.href = `/api/general/read-file?key=${encodeURIComponent(keyPath)}`;
            a.download = obj.name;
            a.target = '_blank'; // Open in new tab if download fails
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (err) {
            setError('Có lỗi xảy ra khi tải file');
            console.error('Download error:', err);
        }
    };

    // Handle copy link
    const handleCopyLink = async (obj: S3Object) => {
        if (obj.isFolder) return;

        console.log('Copying link for object:', `https://s3.hcm-1.cloud.cmctelecom.vn/${obj.key}`);
        try {
                await navigator.clipboard.writeText(`https://s3.hcm-1.cloud.cmctelecom.vn/${obj.key}`);
            } catch (error) {
                throw new Error(`Không thể sao chép text: ${error}`);
            }
   
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedObject) return;
        
        try {
            // Construct the key path for the object
            const keyPath = baseUrl.replace(/\/$/, '') + '/' + (currentPath ? currentPath + '/' : '') + selectedObject.name;
            // For folders, ensure the key ends with '/'
            const objectKey = selectedObject.isFolder && !keyPath.endsWith('/') ? keyPath + '/' : keyPath;
            
            const response = await fileManagerServices.deleteObjects({
                keys: [objectKey],
                recursive: selectedObject.isFolder ? true : undefined
            }, idPath);
            
            if (response?.success) {
                loadObjects(currentPath);
                setShowDeleteModal(false);
                setSelectedObject(null);
                
                // Show success message with deletion count
                if (response.results?.totalDeleted > 0) {
                    console.log(`Đã xóa thành công ${response.results.totalDeleted} objects`);
                }
            } else {
                setError(response?.message || 'Xóa thất bại');
                setShowDeleteModal(false);
            }
        } catch (err) {
            setError('Có lỗi xảy ra khi xóa');
            console.error('Delete error:', err);
        }
    };

    // Filter objects based on search
    const filteredObjects = objects.filter(obj => 
        obj.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Format file size
    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return '-';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Format date
    const formatDate = (dateString?: string): string => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('vi-VN', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="bg-white px-6 py-4 h-full">
            {/* Debug Panel */}
            {/* <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-700">FileManager Debug</h3>
                <p className="text-sm text-gray-600">namepath: {namepath}</p>
                <p className="text-sm text-gray-600">currentPath: {currentPath}</p>
                <p className="text-sm text-gray-600">loading: {loading.toString()}</p>
                <p className="text-sm text-gray-600">error: {error || 'none'}</p>
                <p className="text-sm text-gray-600">objects count: {objects.length}</p>
                <p className="text-sm text-gray-600">baseUrl: {baseUrl}</p>
            </div> */}

            {/* Header */}
            <div className="flex justify-end items-center mb-4">
                {/* <h2 className="text-2xl font-semibold text-gray-800">
                    Quản lý File & Folder
                </h2> */}
                
                <div className="flex gap-2">
                    {allowUpload && (
                        <>
                            <button
                                onClick={() => setShowCreateFolderModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <PlusIcon className="w-5 h-5" />
                                Tạo Folder
                            </button>
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                <CloudArrowUpIcon className="w-5 h-5" />
                                Upload File
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Path Navigation */}
            <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-lg shadow-md">
                <button
                    onClick={navigateToRoot}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                >
                    /upload
                </button>
                {currentPath && (
                    <>
                        {currentPath.split('/').map((folder, index, array) => (
                            <React.Fragment key={index}>
                                <span className="text-gray-400">/</span>
                                <span className="text-gray-600">{folder}</span>
                            </React.Fragment>
                        ))}
                    </>
                )}
                
                {pathHistory.length > 0 && (
                    <button
                        onClick={navigateBack}
                        className="ml-auto flex items-center gap-1 px-3 py-1 text-blue-600 hover:text-blue-800"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Quay lại
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Tìm kiếm file, folder..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            )}

            {/* File List */}
            {!loading && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <div className="max-h-[60vh] overflow-y-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="whitespace-nowrap px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50 z-20 shadow-sm">
                                            Tên
                                        </th>
                                        <th className="whitespace-nowrap px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50 z-20 shadow-sm">
                                            Kích thước
                                        </th>
                                        <th className="whitespace-nowrap px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky top-0 bg-gray-50 z-20 shadow-sm">
                                            Cập nhật
                                        </th>
                                        {(allowDownload || allowDelete || allowCopy) && (
                                            <th className="whitespace-nowrap px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-32 sticky top-0 bg-gray-50 z-20 shadow-sm">
                                                Hành động
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredObjects.length === 0 ? (
                                        <tr>
                                            <td colSpan={(allowDownload || allowDelete || allowCopy) ? 4 : 3} className="px-6 py-8 text-center text-gray-500">
                                                {searchTerm ? 'Không tìm thấy kết quả' : 'Thư mục trống'}
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredObjects.map((obj, index) => (
                                            <tr 
                                                key={index}
                                                className="hover:bg-gray-50 cursor-pointer"
                                                onClick={() => handleObjectClick(obj)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-3">
                                                        {obj.isFolder ? (
                                                            <FolderSolidIcon className="w-6 h-6 text-blue-500" />
                                                        ) : (
                                                            <DocumentSolidIcon className="w-6 h-6 text-gray-500" />
                                                        )}
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {obj.name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {obj.isFolder ? '-' : formatFileSize(obj.size)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(obj.lastModified)}
                                                </td>
                                                {(allowDownload || allowDelete || allowCopy) && (
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            {allowCopy && !obj.isFolder && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleCopyLink(obj);
                                                                    }}
                                                                    style={{ cursor: 'pointer'}}
                                                                    className="inline-flex items-center gap-1 px-2 py-2 bg-white border border-gray-100 text-gray-600 hover:bg-gray-50 rounded-lg"
                                                                    title={`Sao chép link ${obj.name}`}
                                                                >
                                                                    <ClipboardDocumentIcon className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                            {allowDownload && !obj.isFolder && (
                                                              <>
                                                                {/* <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDownload(obj);
                                                                    }}
                                                                    className="inline-flex items-center gap-1 px-2 py-2 bg-white border border-blue-100 text-blue-600 hover:bg-blue-50 rounded-lg"
                                                                    title={`Tải xuống ${obj.name}`}
                                                                >
                                                                    <ArrowDownTrayIcon className="w-4 h-4" />
                                                                </button> */}
                                                              </>
                                                            )}
                                                            {allowDelete && (
                                                                <>
                                                                    <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedObject(obj);
                                                                        setShowDeleteModal(true);
                                                                    }}
                                                                    className="inline-flex items-center gap-1 px-2 py-2 bg-white border border-red-100 text-red-600 hover:bg-red-50 rounded-lg"
                                                                    title={`Xóa ${obj.name}`}
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            <FileUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onUpload={handleUpload}
            />

            <CreateFolderModal
                isOpen={showCreateFolderModal}
                onClose={() => setShowCreateFolderModal(false)}
                onCreateFolder={handleCreateFolder}
            />

            <DeleteConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedObject(null);
                }}
                onConfirm={handleDelete}
                objectName={selectedObject?.name || ''}
                isFolder={selectedObject?.isFolder || false}
            />
        </div>
    );
}
